// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
// Column names for the Status grid page.
export const GRID_TEAM_COLUMN_NAMES = {
    name: 'name',
    vendor: 'vendor',
    pilot: 'pilot',
    teamName: 'teamName',
    carMake: 'carMake',
    carModel: 'carModel',
    carRegistration: 'carRegistration',
    observer: 'observer',
    postProcessingTechnician: 'postProcessingTechnician',
    productionTechnician: 'productionTechnician'
};
// Must match portal-api EnumPriority
export enum PRIORITY_ENUM {
  canceled = 6,
  closed = 5,
  pending = 4,
  in_progress = 3,
  urgent = 2,
  very_urgent = 1
}
// Must match portal-api EnumPriorityExtra
export const PRIORITY_ENUM_Complete = 1001;

export const DEFAULT_RANK = 9999;
export enum JobRequestStatus {
  pending = 1,
  inProgress = 2,
  inProgressWithIssues = 12,
  completed = 3,
  completedWithFailures = 13,
  failed = 4,

  collectionInProgress = 5,
  collectionPartiallyDone = 6,
  collectionDone = 7,
  postProcessing = 8,
  postProcessingFailed = 9,
  postProcessingWithIssues = 10,
  postProcessingDone = 11,

  urgent = 14,
  veryUrgent = 15,
  closedAsComplete = 16,
  closedAsFailed = 17,
  canceled = 18

}

export enum FolderType {
  undefined = 0,
  online = 1,
  offline = 2
}

export const FOLDER_TYPE_TRANSITION_MATRIX = {
  [FolderType.undefined]: FolderType.offline,
  [FolderType.offline]: FolderType.online,
  [FolderType.online]: FolderType.offline
};

export const JOB_REQUEST_STATUS_LABELS = {
  [JobRequestStatus.pending]: 'Pending',
  [JobRequestStatus.inProgress]: 'In progress',
  [JobRequestStatus.inProgressWithIssues]: 'In progress with issues',
  [JobRequestStatus.completed]: 'Complete',
  [JobRequestStatus.completedWithFailures]: 'Complete with failures',
  [JobRequestStatus.failed]: 'Failed',
  [JobRequestStatus.collectionInProgress]: 'Collection in progress',
  [JobRequestStatus.collectionDone]: 'Collection done',
  [JobRequestStatus.collectionPartiallyDone]: 'Collection partially done',
  [JobRequestStatus.postProcessing]: 'Post processing',
  [JobRequestStatus.postProcessingWithIssues]: 'Post processing with issues',
  [JobRequestStatus.postProcessingFailed]: 'Post processing failed',
  [JobRequestStatus.postProcessingDone]: 'Post processing done',
  [JobRequestStatus.urgent]: 'Urgent',
  [JobRequestStatus.veryUrgent]: 'Very urgent',
  [JobRequestStatus.closedAsComplete]: 'Closed as complete',
  [JobRequestStatus.closedAsFailed]: 'Closed as failed',
  [JobRequestStatus.canceled]: 'Canceled'
};

// Translate priority enum value to corresponding jobReq status value.  
export const PRIORITY_ENUM_2_STATUS = {
  [PRIORITY_ENUM.canceled]: JobRequestStatus.canceled,
  [PRIORITY_ENUM.closed]: JobRequestStatus.closedAsFailed, // WXYZ: need verifying
  [PRIORITY_ENUM.pending]: JobRequestStatus.pending,
  [PRIORITY_ENUM.in_progress]: JobRequestStatus.inProgress,
  [PRIORITY_ENUM.urgent]: JobRequestStatus.urgent,
  [PRIORITY_ENUM.very_urgent]: JobRequestStatus.veryUrgent,
}

export const JOB_REQUEST_FIELDS = {
  status: 'status',
  oldStatus: 'oldStatus',
  [GRID_TEAM_COLUMN_NAMES.vendor]: GRID_TEAM_COLUMN_NAMES.vendor,
  name: 'name',
  dueDate: 'dueDate',
  assetType: 'assetType',
  deliverableIdentifiers: 'deliverableIdentifiers',           // list of deliverable checkboxes 
  responsibleVendorId: 'responsibleVendorId',                 // list of vendors (single selection)
  postProcessingTechnicianId: 'postProcessingTechnicianId',   // ditto
  productionTechnicianId: 'productionTechnicianId',           // ditto
  pilotId: 'pilotId',                                         // ditto  
  observerId: 'observerId',                                   // ditto
  assignees: 'assignees',
  isBlocked: 'isBlocked',
  serverName: 'serverName',
  shareName: 'shareName',
  driveLetter: 'driveLetter',
  folderName: 'folderName',
  folderType: 'folderType',
  priority: 'priority',
  tag1: 'tag1',
  tag2: 'tag2',
  lastModificationDate: 'lastModificationDate',
  emi: 'emi',
  multipleTowers: 'multipleTowers',
  highAltitude: 'highAltitude',
  underConstruction: 'underConstruction',
  roadAccessIssues: 'roadAccessIssues',
  otherConditions: 'otherConditions',
  serverNameLiDAR: 'serverNameLiDAR',
  folderNameLiDAR: 'folderNameLiDAR',
  serverNameVisuals: 'serverNameVisuals',
  folderNameVisuals: 'folderNameVisuals',

  // Addtional stuff for asset.
  // SiteInfo:
  legacyId: 'legacyId',
  lat: 'latitude',
  lng: 'longitude',
  height: 'height',
  buildingHeight: 'buildingHeight',
  towerType: 'towerType',
  // SiteInfo-Address
  addressParts: 'addressParts',
  // SiteDetails:
  siteType: 'siteType',
  numberOfTenants: 'numberOfTenants',
  complexityRating: 'complexityRating',
  manufacture: 'manufacture', 
  siteModel: 'siteModel', 
  technology: 'technology', 
  usefulLoadCapacity: 'usefulLoadCapacity', 
  reinforceNReplacement: 'reinforceNReplacement'  
};

// Must match portal-api ByCategory
export const JOB_REQUEST_FILTERS = {
  staff: 'staff',
  assets: 'assets',
  assetsByMap: 'assetsByMap',
  task: 'task',
  priority: 'priority',
  tag1: 'tag1',
  status: 'status',
  flag: 'flag',
  platforms: 'platforms',
  planned: 'planned'
}

// Displayed name for filters.
export const JOB_REQUEST_FILTER_LABELS = {
  [JOB_REQUEST_FILTERS.staff]: 'Staff',
  [JOB_REQUEST_FILTERS.assets]: 'Name',
  [JOB_REQUEST_FILTERS.assetsByMap]: 'Map',
  [JOB_REQUEST_FILTERS.task]: 'Workflow',
  [JOB_REQUEST_FILTERS. priority]: 'Priority',
  [JOB_REQUEST_FILTERS.tag1]: 'Tag',
  [JOB_REQUEST_FILTERS.status]: 'Status',
  [JOB_REQUEST_FILTERS.flag]: 'Flag',
  //[JOB_REQUEST_FILTERS.platforms]: 'Platform',
  [JOB_REQUEST_FILTERS.planned]: 'Plan end'
}

export enum JobRequestSpecialCondition {
  emi = 1,
  multipleTowers = 2,
  highAltitude = 3,
  underConstruction = 4,
  roadAccessIssues = 5,
  otherConditions = 6
}